import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Mail, Phone, UserRound } from 'lucide-react';
import { BottomSheet } from '@shared/ui/containers/BottomSheet';
import { Card } from '@shared/ui/containers/Card';
import { Button } from '@shared/ui/primitives/Button';
import {
  FieldGroup,
  InviteRuleInfo,
  RadioOption,
  SelectField,
  TextInputField,
} from '@shared/ui/forms/FormFields';
import { Banner } from '@shared/ui/feedback/Banner';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { publicBaseUrl } from '@shared/lib/platform';
import { SelectableCardList } from '@shared/ui/forms/SelectableCardList';
import { toast } from '@shared/ui/feedback/Toast';
import { toApiError } from '@shared/api/errors';
import {
  createAccessInvite,
  fetchAccessEntryTypesForInvite,
  fetchAccessInviteLocations,
  fetchAccessInviteSettings,
  type AccessInvite,
  type MemberAccessEntryTypeOption,
} from './api';
import { fetchMemberContacts, type MemberContact } from './memberContactsApi';

type WizardStep = 'type' | 'details';
type VisitorMode = 'saved' | 'adhoc';

const FALLBACK_DEFAULT_VALIDITY_DAYS = 7;
const FALLBACK_MAX_VALIDITY_DAYS = 30;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

interface NewAccessInviteSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialContactId?: string;
  /** Preselect an access type (e.g. opened from a dashboard entry-type tile) — skips the type step. */
  initialEntryTypeId?: string;
  onCreated?: (invite: AccessInvite) => void;
}

export function NewAccessInvitePage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  return (
    <div className="min-h-full bg-paper">
      <NewAccessInviteSheet
        open
        initialContactId={searchParams.get('contact_id') ?? undefined}
        onCreated={(invite) => {
          navigate(`/app/access-invites?invite=${invite.access_invite_id}`, { replace: true });
        }}
        onOpenChange={(open) => {
          if (!open) navigate('/app/access-invites');
        }}
      />
    </div>
  );
}

export function NewAccessInviteSheet({
  open,
  onOpenChange,
  initialContactId,
  initialEntryTypeId,
  onCreated,
}: NewAccessInviteSheetProps): JSX.Element {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<WizardStep>('type');
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [visitorMode, setVisitorMode] = useState<VisitorMode>('saved');
  const [selectedContactId, setSelectedContactId] = useState('');
  const [visitorName, setVisitorName] = useState('');
  const [visitorPhone, setVisitorPhone] = useState('');
  const [visitorEmail, setVisitorEmail] = useState('');
  const [locationId, setLocationId] = useState('');
  const [validForDays, setValidForDays] = useState(String(FALLBACK_DEFAULT_VALIDITY_DAYS));
  const [error, setError] = useState('');

  const typesQuery = useQuery({
    queryKey: ['access-entry-types'],
    queryFn: fetchAccessEntryTypesForInvite,
    enabled: open,
  });
  const locationsQuery = useQuery({
    queryKey: ['access-invite-locations'],
    queryFn: fetchAccessInviteLocations,
    enabled: open,
  });
  const inviteSettingsQuery = useQuery({
    queryKey: ['access-invite-settings'],
    queryFn: fetchAccessInviteSettings,
    enabled: open,
  });
  const contactsQuery = useQuery({
    queryKey: ['member-contacts'],
    queryFn: fetchMemberContacts,
    enabled: open,
  });

  const activeContacts = useMemo(
    () => (contactsQuery.data ?? []).filter((contact) => contact.is_active !== false),
    [contactsQuery.data],
  );
  const selectedType = typesQuery.data?.find((type) => type.access_entry_type_id === selectedTypeId);
  const selfieRequired = Boolean(selectedType?.require_visitor_selfie_default);
  const defaultValidityDays =
    inviteSettingsQuery.data?.default_validity_days ?? FALLBACK_DEFAULT_VALIDITY_DAYS;
  const maxValidityDays = inviteSettingsQuery.data?.max_validity_days ?? FALLBACK_MAX_VALIDITY_DAYS;
  const sheetTitle = step === 'type' ? 'Choose access type' : 'Invite a visitor';
  const sheetDescription =
    step === 'type' ? 'Select the access type for this invite.' : 'Add the visitor and visit details.';

  useEffect(() => {
    if (!open) return;
    // When opened from an entry-type tile, preselect that type and skip the type step.
    setStep(initialEntryTypeId ? 'details' : 'type');
    setVisitorMode('saved');
    setSelectedTypeId(initialEntryTypeId ?? '');
    setSelectedContactId('');
    setVisitorName('');
    setVisitorPhone('');
    setVisitorEmail('');
    setLocationId('');
    setValidForDays(String(defaultValidityDays));
    setError('');
  }, [defaultValidityDays, open, initialEntryTypeId]);

  useEffect(() => {
    if (!open || contactsQuery.isLoading) return;
    const preset = initialContactId && activeContacts.find((contact) => contact.member_contact_id === initialContactId);
    if (preset) {
      setVisitorMode('saved');
      setSelectedContactId(preset.member_contact_id);
      mirrorContact(preset);
      return;
    }
    if (activeContacts.length === 0) setVisitorMode('adhoc');
  }, [activeContacts, contactsQuery.isLoading, initialContactId, open]);

  useEffect(() => {
    const rows = locationsQuery.data ?? [];
    const only = rows.length === 1 ? rows[0] : undefined;
    if (only) {
      setLocationId(only.location_id);
    }
  }, [locationsQuery.data]);

  const mutation = useMutation({
    mutationFn: createAccessInvite,
    onSuccess: (invite) => {
      queryClient.setQueryData<AccessInvite[]>(['access-invites'], (current) => upsertInvite(current, invite));
      void queryClient.invalidateQueries({ queryKey: ['access-invites'] });
      toast.success('Invite created');
      onCreated?.(invite);
      if (!onCreated) onOpenChange(false);
    },
    onError: (e) => {
      const message = toApiError(e).message || 'Create failed';
      setError(message);
      toast.danger("Couldn't create invite", message);
    },
  });

  const selectType = (type: MemberAccessEntryTypeOption) => {
    setSelectedTypeId(type.access_entry_type_id);
    setStep('details');
    setError('');
  };

  const mirrorContact = (contact?: MemberContact) => {
    setVisitorName(contact?.display_name ?? '');
    setVisitorPhone(contact?.phone ?? '');
    setVisitorEmail(contact?.email ?? '');
  };

  const submit = () => {
    if (!selectedTypeId) {
      setError('Select an access type.');
      return;
    }
    if (visitorMode === 'saved' && !selectedContactId) {
      setError('Select a saved visitor or switch to add a new visitor.');
      return;
    }
    const name = visitorName.trim();
    const phone = normalizePhone(visitorPhone);
    const email = visitorEmail.trim();
    if (!name) {
      setError('Enter the visitor name.');
      return;
    }
    if (!phone) {
      setError('Enter the visitor mobile number.');
      return;
    }
    const locations = locationsQuery.data ?? [];
    if (locations.length === 0) {
      setError('No property is linked to your account. Link or verify a property before creating visitor invites.');
      return;
    }
    if (locations.length > 1 && !locationId) {
      setError('Select your property.');
      return;
    }
    const soleLocation = locations.length === 1 ? locations[0] : undefined;
    const resolvedLocationId = locationId || soleLocation?.location_id || '';
    const selectedLocation = locations.find((row) => row.location_id === resolvedLocationId);
    if (selectedLocation && !selectedLocation.location_code?.trim()) {
      setError(
        `${formatLocationLabel(selectedLocation)} has no gate code configured. Ask estate management to set a property code.`,
      );
      return;
    }
    const trimmedValidityDays = validForDays.trim();
    const resolvedValidityDays = trimmedValidityDays ? Number(trimmedValidityDays) : defaultValidityDays;
    if (!Number.isInteger(resolvedValidityDays) || resolvedValidityDays < 1) {
      setError('Enter a whole number of days for invite validity.');
      return;
    }
    if (resolvedValidityDays > maxValidityDays) {
      setError(`Invite validity cannot exceed ${maxValidityDays} days.`);
      return;
    }
    const validUntil = new Date(Date.now() + resolvedValidityDays * MS_PER_DAY).toISOString();
    setError('');
    mutation.mutate({
      access_entry_type_id: selectedTypeId,
      location_id: resolvedLocationId || null,
      member_contact_id: visitorMode === 'saved' ? selectedContactId : null,
      visitor_name: name,
      visitor_phone: phone,
      visitor_email: email || null,
      valid_until: validUntil,
      public_base_url: publicBaseUrl(),
    });
  };

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title={sheetTitle}
      description={sheetDescription}
      height="92dvh"
    >
      {step === 'type' ? (
        <div className="flex flex-col gap-3 pb-4">
          {typesQuery.isLoading ? (
            <>
              <Skeleton className="h-20" />
              <Skeleton className="h-20" />
            </>
          ) : typesQuery.isError ? (
            <Banner variant="danger" title="Could not load access types" />
          ) : (typesQuery.data ?? []).length === 0 ? (
            <Banner title="No access types available" description="Ask estate management to enable visitor invite types." />
          ) : (
            typesQuery.data!.map((type) => (
              <Card
                key={type.access_entry_type_id}
                asChild
                noGrain
                className="rounded-xl p-4 text-left shadow-none transition-colors hover:bg-sunken"
              >
                <button
                  type="button"
                  onClick={() => selectType(type)}
                  className="w-full"
                >
                  <p className="font-display text-headline text-ink">{type.name}</p>
                  {type.code ? (
                    <p className="mt-1 text-caption text-ink-muted">{type.code}</p>
                  ) : null}
                </button>
              </Card>
            ))
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4 pb-4">
          {error ? <Banner variant="danger" title="Check invite details" description={error} /> : null}

          <Card noGrain className="p-4">
            <p className="font-display text-headline text-ink">{selectedType?.name ?? 'Access invite'}</p>
            {selectedType?.code ? (
              <p className="mt-1 text-caption text-ink-muted">{selectedType.code}</p>
            ) : null}
          </Card>

          {activeContacts.length > 0 ? (
            <div className="grid grid-cols-2 gap-2">
              <RadioOption
                label="Saved visitor"
                value="saved"
                selected={visitorMode === 'saved'}
                onChange={() => setVisitorMode('saved')}
              />
              <RadioOption
                label="Add new"
                value="adhoc"
                selected={visitorMode === 'adhoc'}
                onChange={() => {
                  setVisitorMode('adhoc');
                  setSelectedContactId('');
                  mirrorContact(undefined);
                }}
              />
            </div>
          ) : null}

          {visitorMode === 'saved' ? (
            <SelectField
              label="Visitor"
              value={selectedContactId}
              onChange={(event) => {
                const contact = activeContacts.find((row) => row.member_contact_id === event.target.value);
                setSelectedContactId(event.target.value);
                mirrorContact(contact);
              }}
            >
              <option value="">Choose a saved visitor</option>
              {activeContacts.map((contact) => (
                <option key={contact.member_contact_id} value={contact.member_contact_id}>
                  {contact.display_name}
                </option>
              ))}
            </SelectField>
          ) : null}

          <TextInputField
            label="Visitor name"
            value={visitorName}
            onChange={(event) => setVisitorName(event.target.value)}
            startAdornment={<UserRound className="h-4 w-4" strokeWidth={1.5} />}
          />
          <TextInputField
            label="Visitor mobile"
            type="tel"
            inputMode="tel"
            value={visitorPhone}
            onChange={(event) => setVisitorPhone(event.target.value)}
            helper="Used for gate access and invite notifications."
            startAdornment={<Phone className="h-4 w-4" strokeWidth={1.5} />}
          />
          <TextInputField
            label="Visitor email"
            type="email"
            value={visitorEmail}
            onChange={(event) => setVisitorEmail(event.target.value)}
            helper="Optional."
            startAdornment={<Mail className="h-4 w-4" strokeWidth={1.5} />}
          />

          {locationsQuery.isError ? (
            <Banner variant="danger" title="Could not load your properties" />
          ) : locationsQuery.isLoading ? (
            <Skeleton className="h-12" />
          ) : (locationsQuery.data ?? []).length === 0 ? (
            <Banner
              variant="warning"
              title="No property linked"
              description="Link or verify a property on your account before creating visitor invites."
            />
          ) : null}

          {(locationsQuery.data ?? []).length > 0 ? (
            <FieldGroup
              label="Property"
              description={
                (locationsQuery.data ?? []).length > 1
                  ? 'Select the property this visitor will access at the gate.'
                  : 'The visitor will be associated with this property at the gate.'
              }
            >
              <SelectableCardList
                items={(locationsQuery.data ?? []).map((location) => ({
                  id: location.location_id,
                  title: formatLocationLabel(location),
                  subtitle: location.address,
                }))}
                selectedId={locationId}
                onSelect={setLocationId}
              />
            </FieldGroup>
          ) : null}

          <TextInputField
            label="Valid for"
            type="number"
            inputMode="numeric"
            min={1}
            max={maxValidityDays}
            step={1}
            value={validForDays}
            onChange={(event) => setValidForDays(event.target.value)}
            helper={`Defaults to ${defaultValidityDays} ${defaultValidityDays === 1 ? 'day' : 'days'}. Maximum ${maxValidityDays} days.`}
            startAdornment={<CalendarDays className="h-4 w-4" strokeWidth={1.5} />}
            endAdornment={<span className="text-caption">days</span>}
          />

          <div className="flex flex-col gap-2">
            <p className="text-caption font-medium uppercase tracking-wide text-ink-muted">Visit rules</p>
            <InviteRuleInfo
              label="Single entry"
              description="The visitor can use the gate once during the validity period."
              active
            />
            <InviteRuleInfo
              label="Require visitor selfie"
              description={
                selfieRequired
                  ? 'The visitor must capture a photo before the invite can be used.'
                  : 'No visitor selfie is required for this access type.'
              }
              active={selfieRequired}
            />
          </div>

          <div className="mt-2 grid grid-cols-2 gap-3">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button loading={mutation.isPending} onClick={submit}>Create invite</Button>
          </div>
        </div>
      )}
    </BottomSheet>
  );
}

function upsertInvite(current: AccessInvite[] | undefined, invite: AccessInvite): AccessInvite[] {
  if (!current) return [invite];
  return [...current.filter((row) => row.access_invite_id !== invite.access_invite_id), invite];
}

function normalizePhone(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  if (value.trim().startsWith('+')) return `+${digits}`;
  if (digits.startsWith('27')) return `+${digits}`;
  return `+27${digits.replace(/^0+/, '')}`;
}

function formatLocationLabel(location: {
  name?: string | null;
  location_code?: string | null;
  label?: string | null;
}): string {
  const name = location.name ?? location.label ?? 'Property';
  const code = location.location_code?.trim();
  return code ? `${name} (${code})` : name;
}
